import { getRequestTransportErrorMessage } from '#/utils/requestError';
import { toast } from '#/utils/toast';

export const ATTACHMENT_LIST_QUERY = Object.freeze({ page: 1, pageSize: 50 });

export const ATTACHMENT_UPLOAD_LIMITS = Object.freeze({
  maxFilesPerPick: 10,
  maxTotalBytes: 100 * 1024 * 1024,
  maxConcurrent: 3,
});

/**
 * @param {number | null | undefined} bytes
 */
export function formatAttachmentSize(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) {
    return '—';
  }
  if (n < 1024) {
    return `${n} B`;
  }
  if (n < 1024 * 1024) {
    return `${(n / 1024).toFixed(1)} KB`;
  }
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * @param {File[]} files
 * @returns {{ ok: true, files: File[] } | { ok: false, message: string }}
 */
export function validateAttachmentPick(files) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (list.length === 0) {
    return { ok: false, message: '請選擇檔案' };
  }
  if (list.length > ATTACHMENT_UPLOAD_LIMITS.maxFilesPerPick) {
    return {
      ok: false,
      message: `一次最多選 ${ATTACHMENT_UPLOAD_LIMITS.maxFilesPerPick} 個檔案`,
    };
  }
  const total = list.reduce((sum, file) => sum + (file.size || 0), 0);
  if (total > ATTACHMENT_UPLOAD_LIMITS.maxTotalBytes) {
    return { ok: false, message: '選取檔案合計不可超過 100 MB' };
  }
  return { ok: true, files: list };
}

/**
 * 同時最多 maxConcurrent；其餘排隊。成功／失敗獨立。
 * @template T
 * @param {T[]} items
 * @param {(item: T) => Promise<unknown>} worker
 * @param {number} [maxConcurrent]
 */
export async function runWithConcurrency(items, worker, maxConcurrent = 3) {
  const queue = [...items];
  const results = [];

  async function pump() {
    while (queue.length > 0) {
      const item = queue.shift();
      try {
        results.push({ ok: true, item, value: await worker(item) });
      } catch (error) {
        results.push({ ok: false, item, error });
      }
    }
  }

  const workers = Array.from(
    { length: Math.min(maxConcurrent, items.length) },
    () => pump(),
  );
  await Promise.all(workers);
  return results;
}

export function documentAttachmentsQueryKey(ownerKey, businessId) {
  return ['erp', 'documentAttachments', ownerKey, businessId, ATTACHMENT_LIST_QUERY];
}

/**
 * 將新選取合併進既有 pending，並套用一次選取上限。
 * @param {File[]} existing
 * @param {File[]} picked
 * @returns {{ ok: true, files: File[] } | { ok: false, message: string }}
 */
export function mergePendingAttachmentPick(existing, picked) {
  const current = Array.isArray(existing) ? existing.filter(Boolean) : [];
  const nextPick = Array.isArray(picked) ? picked.filter(Boolean) : [];
  return validateAttachmentPick([...current, ...nextPick]);
}

/**
 * 建立 upload batch 後併發上傳。單檔失敗不中斷其餘檔。
 * batch 建立失敗會 throw（呼叫端可決定是否回滾單據）。
 *
 * @param {{ createUploadBatch: Function, upload: Function }} api
 * @param {string} businessId
 * @param {File[]} files
 * @returns {Promise<Array<{ ok: boolean, item: File, value?: unknown, error?: unknown }>>}
 */
export async function uploadAttachmentFiles(api, businessId, files) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (list.length === 0) {
    return [];
  }

  const batch = await api.createUploadBatch(businessId, {
    fileCount: list.length,
    totalSizeBytes: list.reduce((sum, file) => sum + (file.size || 0), 0),
  });
  const uploadBatchId = batch?.data?.uploadBatchId;
  if (!uploadBatchId) {
    throw new Error('無法取得上傳批次');
  }

  return runWithConcurrency(
    list,
    (file) => api.upload(businessId, { uploadBatchId, file }),
    ATTACHMENT_UPLOAD_LIMITS.maxConcurrent,
  );
}

/**
 * @param {Array<{ ok: boolean }>|null|undefined} results
 * @returns {{ okCount: number, failCount: number }}
 */
export function summarizeUploadAttachmentResults(results) {
  const rows = Array.isArray(results) ? results : [];
  const okCount = rows.filter((row) => row.ok).length;
  return { okCount, failCount: rows.length - okCount };
}

/**
 * 單據 create 成功後上傳暫存檔；失敗只 toast，不 throw（不回滾單據）。
 *
 * @param {{ createUploadBatch: Function, upload: Function }} api
 * @param {string} businessId
 * @param {File[]} files
 */
export async function uploadPendingAttachmentsAfterCreate(api, businessId, files) {
  const list = Array.isArray(files) ? files.filter(Boolean) : [];
  if (!businessId || list.length === 0) {
    return { skipped: true };
  }

  try {
    const results = await uploadAttachmentFiles(api, businessId, list);
    const { okCount, failCount } = summarizeUploadAttachmentResults(results);
    if (okCount > 0) {
      toast.success(failCount > 0
        ? `已上傳 ${okCount} 個附件，${failCount} 個失敗`
        : `已上傳 ${okCount} 個附件`);
    } else if (failCount > 0) {
      toast.error('附件上傳失敗');
    }
    return { results };
  } catch (error) {
    const transport = getRequestTransportErrorMessage(error);
    toast.error(
      transport || error?.response?.data?.message || error?.message || '附件上傳失敗',
    );
    return { error };
  }
}
